import React from 'react';

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches render-time errors inside the member shell.
 *
 * Before this existed, any throw in a routed page unmounted the entire React
 * tree — the nav rail included — leaving a blank white page with nothing but a
 * console error, and no route the member could navigate to in order to recover.
 * A missing `personalInfo.address` on the family-history contact screen did
 * exactly that for most members.
 *
 * Mirrors `apps/doctors/src/components/PageErrorBoundary.tsx`, restyled for the
 * member app's design tokens. The consumer keys this on the pathname so
 * navigating to another page clears the error automatically.
 */
export default class MemberErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('Member page render error:', error, info.componentStack);
  }

  handleRetry = () => {
    this.setState({ error: null });
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="px-6 md:px-12 py-12">
        <div
          className="max-w-xl mx-auto rounded-xl border p-6"
          style={{ borderColor: 'var(--melio-border)', backgroundColor: 'white' }}
        >
          <h1
            className="text-xl font-normal mb-3"
            style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}
          >
            Something went wrong on this page.
          </h1>
          <p className="text-sm mb-5" style={{ color: 'var(--melio-text-muted)' }}>
            Your information is safe — this is a display problem, not a data one. Try again, or
            head back to your dashboard and we&apos;ll pick things up from there.
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={this.handleRetry}
              className="px-5 py-3 rounded text-sm font-medium transition-opacity hover:opacity-90"
              style={{ backgroundColor: 'var(--melio-charcoal)', color: 'var(--melio-cream)' }}
            >
              Try again
            </button>
            <a
              href="/home"
              className="px-5 py-3 rounded text-sm font-medium border transition-opacity hover:opacity-90"
              style={{ borderColor: 'var(--melio-border)', color: 'var(--melio-charcoal)' }}
            >
              Back to dashboard
            </a>
          </div>
        </div>
      </div>
    );
  }
}
